import type { OnboardingChecklist } from '@/types/onboarding-checklist';

export const defaultOnboardingChecklist: OnboardingChecklist = {
  groups: [
    {
      id: 'ongoing-support',
      title: '💬 Ongoing Support Structure',
      description: 'Establish ongoing support and check-ins',
      order: 0,
      items: [
        {
          id: 'custom-1760085124848',
          title: 'Set up users & roles',
          description: '',
          order: 0,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'manage-users',
        },
        {
          id: 'check-in-cadence',
          title: 'Set Check-in cadence',
          order: 1,
          completed: false,
          calculationType: 'manual',
        },
      ],
    },
    {
      id: 'initial-setup',
      title: '👖 Product Creation',
      description: 'Essential configurations to get started with the system',
      order: 1,
      items: [
        {
          id: 'article-number-logic',
          title: 'Article number logic',
          description: 'How should an article be named, factoring in product ID, size, color etc.',
          order: 0,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'article-numbers',
        },
        {
          id: 'hierarchy',
          title: 'Configure Hierarchy',
          description: 'Review suggested hierarchy, tailor to match needs',
          order: 1,
          completed: false,
          calculationType: 'hierarchy-nodes-count',
          targetValue: 1,
          adminPageRoute: 'hierarchy-nodes',
        },
        {
          id: 'custom-1760084676104',
          title: 'Attributes / Tags',
          description: '',
          order: 2,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'tags',
        },
        {
          id: 'supporting-documentation',
          title: 'Size Systems & model size',
          description: 'What are the size systems and ranges that will be used? Implement into system',
          order: 3,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'size-systems',
        },
        {
          id: 'custom-1760082571260',
          title: 'Color Group confirmation',
          description: 'Check that color groups are reasonable',
          order: 4,
          completed: false,
          calculationType: 'color-groups-count',
          targetValue: 1,
          adminPageRoute: 'color-groups',
        },
        {
          id: 'custom-1760272084013',
          title: 'Add color palettes',
          description: 'Pantone and customized',
          order: 5,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'color-palettes',
        },
        {
          id: 'custom-1760082425152',
          title: 'Seasons',
          description: 'Add seasons and confirm date ranges.',
          order: 6,
          completed: false,
          calculationType: 'seasons-count',
          targetValue: 5,
          adminPageRoute: 'seasons',
        },
        {
          id: 'custom-1760172734596',
          title: 'Set up other fields needed',
          description: '',
          order: 7,
          completed: false,
          calculationType: 'manual',
        },
        {
          id: 'custom-1760082628167',
          title: 'Check Article Statuses',
          description: 'Confirm that the current statuses are relevant',
          order: 8,
          completed: false,
          calculationType: 'article-statuses-count',
          targetValue: 4,
          adminPageRoute: 'article-status',
        },
        {
          id: 'custom-1760271997147',
          title: 'Article statuses: set triggered events & mandatory fields by status',
          description: 'Trigger actions at a status, or make fields mandatory to change status',
          order: 9,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'article-status',
        },
        {
          id: 'custom-1760272457788',
          title: 'Review user permission to set statuses.',
          description: '',
          order: 10,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'permissions',
        },
        {
          id: 'set-priorities',
          title: 'Supplier import',
          description: 'Name, country of origin as minimum',
          order: 11,
          completed: false,
          calculationType: 'suppliers-count',
          targetValue: 1,
        },
        {
          id: 'custom-1760296505875',
          title: 'EAN code range',
          description: 'Full untouched EAN range',
          order: 12,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'sku-codes',
        },
        {
          id: 'custom-timeline',
          title: 'Timeline',
          description: 'Set up timelines and milestones per season',
          order: 13,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'timelines',
        },
      ],
    },
    {
      id: 'pricing',
      title: '💰 Pricing',
      description: 'Pricing and margin setup',
      order: 2,
      items: [
        {
          id: 'price-logic',
          title: 'Price logic & set up',
          description:
            'Customer provides their price logic and margin calculation to Resourced, Resourced then configures the price set up in the system',
          order: 0,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'price-configs',
        },
        {
          id: 'custom-1760082444590',
          title: 'Currencies and exchange rate',
          description: 'Add currencies and confirm default currency',
          order: 1,
          completed: false,
          calculationType: 'currencies-count',
          targetValue: 2,
          adminPageRoute: 'currencies-and-exchange-rates',
        },
        {
          id: 'custom-1787232914777',
          title: 'Mode of Transport & Freight',
          description: 'Add modes of transport and freight costs, used by the price calculator',
          order: 2,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'mode-of-transports',
        },
        {
          id: 'custom-1787232930405',
          title: 'Tariffs',
          description: 'Add tariff codes and duty rates, used by the price calculator',
          order: 3,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'tariff-codes',
        },
        {
          id: 'custom-1782208159349',
          title: 'Margin & markup targets',
          description: 'Set target margins and markups to compare calculated prices against',
          order: 4,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'markup-margin-targets',
        },
      ],
    },
    {
      id: 'product-import',
      title: '📥 Product Import',
      description: 'Import product data once configuration and pricing are in place',
      order: 3,
      items: [
        {
          id: 'custom-1760083086550',
          title: 'Import Product Data',
          description: '',
          order: 0,
          completed: false,
          calculationType: 'manual',
        },
        {
          id: 'custom-1760172789196',
          title: 'Confirm data structure in Resourced',
          description: '',
          order: 1,
          completed: false,
          calculationType: 'manual',
        },
      ],
    },
    {
      id: 'components',
      title: '🧵 Materials & Components',
      description: 'Materials, components, BOM and measurement list setup',
      order: 4,
      items: [
        {
          id: 'component-reference',
          title: 'Fibers/Materials: Sense check.',
          description: 'Check that the list covers the needs and not too many are there.',
          order: 0,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'composition-materials',
        },
        {
          id: 'nomenclature',
          title: 'Component types: Sense check.',
          description: 'Check that the type concept seems clear.',
          order: 1,
          completed: false,
          calculationType: 'component-types-count',
          targetValue: 1,
          adminPageRoute: 'component-types',
        },
        {
          id: 'custom-1760082971286',
          title: 'Component statuses: Sense check',
          description: 'Check that component statuses makes sense.',
          order: 2,
          completed: false,
          calculationType: 'component-statuses-count',
          targetValue: 3,
          adminPageRoute: 'component-status',
        },
        {
          id: 'custom-1760271119545',
          title: 'Certificates: Add standards.',
          description: '',
          order: 3,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'certificates',
        },
        {
          id: 'custom-1760271076440',
          title: 'Compliance / testing: Add standards.',
          description: '',
          order: 4,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'compliance',
        },
        {
          id: 'custom-1760271171739',
          title: 'Component tags: Review and add',
          description: '',
          order: 5,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'component-tags',
        },
        {
          id: 'custom-1760271916296',
          title: 'Import components',
          description: '',
          order: 6,
          completed: false,
          calculationType: 'manual',
        },
      ],
    },
    {
      id: 'scope2-bom-measurement',
      title: '📐 BOM & Measurement lists',
      description: 'BOM and measurement list setup',
      order: 5,
      items: [
        {
          id: 'custom-1760272015913',
          title: 'Set up saved bom sections',
          description: '',
          order: 0,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'saved-sections',
        },
        {
          id: 'custom-1760272025047',
          title: 'Set up default BOMs per hierarchy',
          description: '',
          order: 1,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'default-bom-templates',
        },
        {
          id: 'custom-1760270879490',
          title: 'Measurement points & standard grading',
          description: '',
          order: 2,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'measurements',
        },
        {
          id: 'custom-1760270700756',
          title: 'Export: BOM & M-chart layout',
          description: '',
          order: 3,
          completed: false,
          calculationType: 'manual',
        },
      ],
    },
    {
      id: 'sample-handling',
      title: '🎨 Sample Handling',
      description: 'Setup for sample management',
      order: 6,
      items: [
        {
          id: 'sample-types',
          title: 'Defining sample types & document send out per sample type',
          order: 0,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'sample-round-types',
        },
      ],
    },
    {
      id: 'suppliers',
      title: '🚚 Suppliers',
      description: 'Supplier setup',
      order: 7,
      items: [
        {
          id: 'custom-1760271408026',
          title: 'Supplier statuses: Sense check',
          description: 'Sense check',
          order: 0,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'supplier-status',
        },
        {
          id: 'custom-1760271423157',
          title: 'Payment terms: Sense check',
          description: 'Sense check',
          order: 1,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'payment-terms',
        },
        {
          id: 'custom-1760271437924',
          title: 'Incoterms / delivery terms: Sense check',
          description: 'Sense check',
          order: 2,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'delivery-terms',
        },
        {
          id: 'custom-supplier-data',
          title: 'Complete supplier data',
          description: 'Fill in all needed info per supplier in the library',
          order: 3,
          completed: false,
          calculationType: 'manual',
        },
      ],
    },
    {
      id: 'implementation-product',
      title: '📦 Training',
      description: 'Training and implementation across product, tech packs and samples',
      order: 8,
      items: [
        {
          id: 'training-product-filters',
          title: 'Training: Product creation and attribution',
          order: 0,
          completed: false,
          calculationType: 'manual',
        },
        {
          id: 'uat-process',
          title: 'User acceptance testing Product creation.',
          order: 1,
          completed: false,
          calculationType: 'manual',
        },
        {
          id: 'training-bom',
          title: 'Training: Components & Bill of materials / tech packs',
          order: 2,
          completed: false,
          calculationType: 'manual',
        },
        {
          id: 'custom-uat-techpack',
          title: 'User acceptance testing Components & Bill of materials / tech packs',
          order: 3,
          completed: false,
          calculationType: 'manual',
        },
        {
          id: 'training-sample-sending',
          title: 'Training: Sending out and handling samples',
          order: 4,
          completed: false,
          calculationType: 'manual',
        },
        {
          id: 'uat-sample',
          title: 'User acceptance testing Sample handling',
          order: 5,
          completed: false,
          calculationType: 'manual',
        },
      ],
    },
    {
      id: 'detailed-setup',
      title: '🏷️ Overviews, Labels & Reporting',
      description: 'Labels, exports and overviews',
      order: 9,
      items: [
        {
          id: 'labels-stickers',
          title: 'Labels and Stickers',
          description:
            'Provide examples and guidance to Resourced on what stickers and care labels are needed (Content, Dimensions etc.)',
          order: 0,
          completed: false,
          calculationType: 'manual',
        },
        {
          id: 'custom-1760172829982',
          title: 'Export: Article / SKU layout',
          description: '',
          order: 1,
          completed: false,
          calculationType: 'manual',
        },
        {
          id: 'custom-1760172975314',
          title: 'Export: Range plan layout',
          description: '',
          order: 2,
          completed: false,
          calculationType: 'manual',
        },
      ],
    },
    {
      id: 'forecast-management',
      title: '📊 Forecast & Order Management',
      description: 'Order and forecast management setup',
      order: 10,
      items: [
        {
          id: 'custom-1760272613039',
          title: 'Order statuses: Review',
          description: '',
          order: 0,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'order-status',
        },
        {
          id: 'custom-1760272636670',
          title: 'Order Types: Review',
          description: '',
          order: 1,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'order-types',
        },
        {
          id: 'custom-1760272645620',
          title: 'Order Tags: Review',
          description: '',
          order: 2,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'order-tags',
        },
        {
          id: 'training-analysis',
          title: 'Export: set up order sheet layout',
          order: 3,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'excel-templates',
        },
        {
          id: 'custom-1760272735054',
          title: 'Export: review order analysis',
          description: '',
          order: 4,
          completed: false,
          calculationType: 'manual',
          adminPageRoute: 'excel-templates',
        },
        {
          id: 'training-orders',
          title: 'Training: Creating and handling orders & forecasts',
          order: 5,
          completed: false,
          calculationType: 'manual',
        },
        {
          id: 'uat-order',
          title: 'User acceptance testing order management',
          order: 6,
          completed: false,
          calculationType: 'manual',
        },
      ],
    },
    {
      id: 'custom-group-1760272772837',
      title: 'Other systems',
      description: 'Map other systems and which system is master for which info',
      order: 11,
      items: [
        {
          id: 'custom-1760272778087',
          title: 'Review need and other systems map.',
          description: '',
          order: 0,
          completed: false,
          calculationType: 'manual',
        },
        {
          id: 'custom-1760272787160',
          title: 'Define what Resourced should be master for.',
          description: '',
          order: 1,
          completed: false,
          calculationType: 'manual',
        },
        {
          id: 'custom-1760272799370',
          title: 'Structure exports for other systems or imports.',
          description: '',
          order: 2,
          completed: false,
          calculationType: 'manual',
        },
      ],
    },
  ],
};
